import type { ReactNode } from "react";
import { AlertCircle, ArrowDown, ArrowDownRight, ArrowUp, ArrowUpDown, ArrowUpRight, ExternalLink, LoaderCircle, Minus } from "lucide-react";
import type { ProductSortKey, SortDirection } from "../lib/sorting";

export function Loading({ label = "Loading" }: { label?: string }) {
  return <div className="state-card"><LoaderCircle className="spin" size={20} /><span>{label}…</span></div>;
}

export function EmptyState({ title, detail }: { title: string; detail: string }) {
  return <div className="empty-state"><div className="empty-icon"><AlertCircle size={18} /></div><strong>{title}</strong><p>{detail}</p></div>;
}

export function ErrorState({ message, retry }: { message: string; retry?: () => void }) {
  return <div className="error-state" role="alert"><AlertCircle size={20} /><div><strong>Something went wrong</strong><p>{message}</p></div>{retry && <button className="button secondary" onClick={retry}>Try again</button>}</div>;
}

export function PageHeader({ eyebrow, title, description, actions }: { eyebrow?: string; title: string; description: string; actions?: ReactNode }) {
  return <header className="page-header"><div>{eyebrow && <span className="eyebrow">{eyebrow}</span>}<h1>{title}</h1><p>{description}</p></div>{actions && <div className="page-actions">{actions}</div>}</header>;
}

export type FavourableDirection = "increase" | "decrease";

export function Change({ value, favourable = "increase" }: { value: number | null; favourable?: FavourableDirection }) {
  if (value === null) return <span className="change neutral">No previous-period data</span>;
  if (value === 0) return <span className="change neutral"><Minus size={14} />0.0% <em>vs previous period</em></span>;
  const improving = favourable === "increase" ? value > 0 : value < 0;
  return <span className={`change ${improving ? "positive" : "negative"}`}>{value > 0 ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}{Math.abs(value).toFixed(1)}% <em>vs previous period</em></span>;
}

export function SourceLink({ href }: { href: string }) {
  return <a className="source-link" href={href} target="_blank" rel="noreferrer">View in Discord <ExternalLink size={13} /></a>;
}

export function Pagination({ page, pageSize, total, onPage }: { page: number; pageSize: number; total: number; onPage: (page: number) => void }) {
  const pages = Math.max(Math.ceil(total / pageSize), 1);
  return <div className="pagination"><span>{total.toLocaleString("en-GB")} records</span><div><button className="button secondary small" disabled={page <= 1} onClick={() => onPage(page - 1)}>Previous</button><span>Page {page} of {pages}</span><button className="button secondary small" disabled={page >= pages} onClick={() => onPage(page + 1)}>Next</button></div></div>;
}

export function SortableHeader({ label, sortKey, activeKey, direction, onSort }: {
  label: string;
  sortKey: ProductSortKey;
  activeKey: ProductSortKey;
  direction: SortDirection;
  onSort: (key: ProductSortKey) => void;
}) {
  const active = sortKey === activeKey;
  const ariaSort = active ? (direction === "asc" ? "ascending" : "descending") : "none";
  const Icon = !active ? ArrowUpDown : direction === "asc" ? ArrowUp : ArrowDown;
  return <th aria-sort={ariaSort}><button type="button" className={`sort-header ${active ? "active" : ""}`} onClick={() => onSort(sortKey)}>{label}<Icon size={12}/></button></th>;
}
